import React, { useMemo, useState } from 'react';
import { ChevronDown, TrendingUp, TrendingDown, Activity, MessageSquare, Shield, Heart, Sparkles } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { Scan } from '../../types/scan';
interface CommunicationStyleWidgetProps {
  scans: Scan[];
}
export function CommunicationStyleWidget({
  scans
}: CommunicationStyleWidgetProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const [hoveredPoint, setHoveredPoint] = useState<{
    traitIndex: number;
    pointIndex: number;
    value: number;
    x: number;
    y: number;
  } | null>(null);
  // Helper function to safely get patterns array
  const getPatterns = (scan: Scan): any[] => {
    if (!scan.patterns) return [];
    if (Array.isArray(scan.patterns)) return scan.patterns;
    // If patterns is a string, try to parse it
    if (typeof scan.patterns === 'string') {
      try {
        const parsed = JSON.parse(scan.patterns);
        return Array.isArray(parsed) ? parsed : [];
      } catch {
        return [];
      }
    }
    // If patterns is an object, wrap it in an array
    if (typeof scan.patterns === 'object') {
      return [scan.patterns];
    }
    return [];
  };
  // Analyze real scan data to derive communication metrics
  const communicationData = useMemo(() => {
    const completedScans = scans.filter(s => s.status === 'completed' && s.patterns);
    if (completedScans.length === 0) {
      return null;
    }
    // Map pattern types to communication traits
    const calculateTraitScore = (patternTypes: string[]) => {
      const assertivenessPatterns = ['passive-aggressive', 'indirect-communication', 'avoidance'];
      const empathyPatterns = ['dismissive', 'invalidation', 'lack-of-empathy', 'gaslighting'];
      const boundaryPatterns = ['manipulation', 'guilt-tripping', 'boundary-violation', 'control'];
      const assertiveness = 100 - patternTypes.filter(p => assertivenessPatterns.some(ap => p.toLowerCase().includes(ap))).length * 15;
      const empathy = 100 - patternTypes.filter(p => empathyPatterns.some(ep => p.toLowerCase().includes(ep))).length * 15;
      const boundarySetting = 100 - patternTypes.filter(p => boundaryPatterns.some(bp => p.toLowerCase().includes(bp))).length * 15;
      return {
        assertiveness: Math.max(20, Math.min(100, assertiveness)),
        empathy: Math.max(20, Math.min(100, empathy)),
        boundarySetting: Math.max(20, Math.min(100, boundarySetting))
      };
    };
    // Get last 5 scans for trend analysis (chronological: oldest → newest)
    const recentScans = completedScans.slice(0, 5).reverse();
    const assertivenessValues: number[] = [];
    const empathyValues: number[] = [];
    const boundaryValues: number[] = [];
    recentScans.forEach(scan => {
      const patterns = getPatterns(scan);
      const patternTypes = patterns.map(p => p.type || '');
      const scores = calculateTraitScore(patternTypes);
      assertivenessValues.push(scores.assertiveness);
      empathyValues.push(scores.empathy);
      boundaryValues.push(scores.boundarySetting);
    });
    // No synthetic padding — chart renders only actual user scan data points.
    // This avoids a misleading flat line at a single repeated value when the
    // user has fewer than 5 scans (which previously produced "always 100" looking charts).
    const currentAssertiveness = assertivenessValues[assertivenessValues.length - 1];
    const currentEmpathy = empathyValues[empathyValues.length - 1];
    const currentBoundary = boundaryValues[boundaryValues.length - 1];
    // Determine persona based on current scores
    const avgScore = (currentAssertiveness + currentEmpathy + currentBoundary) / 3;
    let persona = 'Balanced Communicator';
    if (currentAssertiveness >= 70 && currentBoundary >= 70) {
      persona = 'Assertive Communicator';
    } else if (currentEmpathy >= 75) {
      persona = 'Empathetic Communicator';
    } else if (currentBoundary >= 70) {
      persona = 'Boundary-Conscious Communicator';
    } else if (avgScore < 50) {
      persona = 'Developing Communicator';
    }
    // Calculate changes from previous scan
    const changes: any[] = [];
    if (recentScans.length >= 2) {
      const prevAssertiveness = assertivenessValues[assertivenessValues.length - 2];
      const prevEmpathy = empathyValues[empathyValues.length - 2];
      const prevBoundary = boundaryValues[boundaryValues.length - 2];
      const assertivenessChange = currentAssertiveness - prevAssertiveness;
      const empathyChange = currentEmpathy - prevEmpathy;
      const boundaryChange = currentBoundary - prevBoundary;
      if (Math.abs(assertivenessChange) >= 5) {
        changes.push({
          trait: 'Assertiveness',
          change: Math.abs(Math.round(assertivenessChange)),
          direction: assertivenessChange > 0 ? 'up' : 'down',
          example: assertivenessChange > 0 ? 'More direct communication patterns detected in recent conversations' : 'Less assertive language patterns observed',
          scanRef: `#${completedScans[0].id.slice(0, 6)}`
        });
      }
      if (Math.abs(empathyChange) >= 5) {
        changes.push({
          trait: 'Empathy',
          change: Math.abs(Math.round(empathyChange)),
          direction: empathyChange > 0 ? 'up' : 'down',
          example: empathyChange > 0 ? 'More active listening and validation markers detected' : 'Fewer empathetic response patterns observed',
          scanRef: `#${completedScans[0].id.slice(0, 6)}`
        });
      }
      if (Math.abs(boundaryChange) >= 5) {
        changes.push({
          trait: 'Boundary-Setting',
          change: Math.abs(Math.round(boundaryChange)),
          direction: boundaryChange > 0 ? 'up' : 'down',
          example: boundaryChange > 0 ? 'Clearer boundary-setting language detected in responses' : 'Less defined boundary communication observed',
          scanRef: `#${completedScans[0].id.slice(0, 6)}`
        });
      }
    }
    // Check if persona recently evolved (comparing first and last scan)
    const firstPatterns = getPatterns(recentScans[0]);
    const lastPatterns = getPatterns(recentScans[recentScans.length - 1]);
    const firstScores = calculateTraitScore(firstPatterns.map(p => p.type || ''));
    const lastScores = calculateTraitScore(lastPatterns.map(p => p.type || ''));
    const avgChange = Math.abs((lastScores.assertiveness - firstScores.assertiveness + (lastScores.empathy - firstScores.empathy) + (lastScores.boundarySetting - firstScores.boundarySetting)) / 3);
    const recentlyEvolved = avgChange >= 15;
    return {
      currentPersona: persona,
      confidence: Math.round(avgScore),
      recentlyEvolved,
      traits: [{
        name: 'Assertiveness',
        values: assertivenessValues,
        color: '#DC2626',
        icon: MessageSquare,
        currentValue: Math.round(currentAssertiveness)
      }, {
        name: 'Empathy',
        values: empathyValues,
        color: '#10B981',
        icon: Heart,
        currentValue: Math.round(currentEmpathy)
      }, {
        name: 'Boundary-Setting',
        values: boundaryValues,
        color: '#9333EA',
        icon: Shield,
        currentValue: Math.round(currentBoundary)
      }],
      recentChanges: changes,
      scanCount: completedScans.length
    };
  }, [scans]);
  // If no data available, show empty state
  if (!communicationData) {
    return <div className="relative bg-[#0a0a0a] border border-gray-800 overflow-hidden mb-8" data-id="element-731">
        <div className="absolute top-0 left-0 w-4 h-4 border-t-2 border-l-2 border-gray-600" data-id="element-732" />
        <div className="absolute top-0 right-0 w-4 h-4 border-t-2 border-r-2 border-gray-600" data-id="element-733" />
        <div className="absolute bottom-0 left-0 w-4 h-4 border-b-2 border-l-2 border-gray-600" data-id="element-734" />
        <div className="absolute bottom-0 right-0 w-4 h-4 border-b-2 border-r-2 border-gray-600" data-id="element-735" />

        <div className="p-8 text-center" data-id="element-736">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-[#FF0000]/10 border border-[#FF0000] mb-4" data-id="element-737">
            <Activity className="w-8 h-8 text-[#FF0000]" data-id="element-738" />
          </div>
          <h3 className="text-lg font-black text-white uppercase tracking-tight mb-2" data-id="element-739">
            Communication Forensics
          </h3>
          <p className="text-gray-400 font-mono text-sm mb-6" data-id="element-740">
            Initialize first scan to generate behavioral communication profile
          </p>
        </div>
      </div>;
  }
  // Chart dimensions
  const width = 100;
  const height = 40;
  const padding = 2;
  // Helper to scale values to SVG coordinates
  // Guard: when only 1 data point exists, center it horizontally to avoid NaN.
  const getX = (index: number, total: number) => total <= 1 ? width / 2 : index / (total - 1) * (width - padding * 2) + padding;
  const getY = (value: number) => height - value / 100 * (height - padding * 2) - padding;
  // Generate path string for a line
  const getPath = (values: number[]) => {
    return values.map((val, i) => `${i === 0 ? 'M' : 'L'} ${getX(i, values.length)} ${getY(val)}`).join(' ');
  };
  const topChange = communicationData.recentChanges[0];
  return <div className="relative bg-[#0a0a0a] border border-gray-800 overflow-hidden mb-8" data-id="element-741">
      <div className="absolute top-0 left-0 w-4 h-4 border-t-2 border-l-2 border-gray-600" data-id="element-742" />
      <div className="absolute top-0 right-0 w-4 h-4 border-t-2 border-r-2 border-gray-600" data-id="element-743" />
      <div className="absolute bottom-0 left-0 w-4 h-4 border-b-2 border-l-2 border-gray-600" data-id="element-744" />
      <div className="absolute bottom-0 right-0 w-4 h-4 border-b-2 border-r-2 border-gray-600" data-id="element-745" />

      {/* PRIMARY: Current Persona - Hero Section */}
      <div className="p-6 space-y-4 border-b border-gray-800" data-id="element-746">
        <div className="flex items-start justify-between gap-4" data-id="element-747">
          <div className="flex-1 space-y-2" data-id="element-748">
            {/* Label - Tertiary */}
            <div className="flex items-center gap-2" data-id="element-749">
              <Activity className="w-4 h-4 text-[#FF0000]" data-id="element-750" />
              <span className="text-sm font-mono text-gray-500 uppercase tracking-widest leading-tight" data-id="element-751">
                Subject Communication Profile
              </span>
            </div>

            {/* Persona Title - PRIMARY (text-2xl font-bold) */}
            <h2 className="text-2xl font-black text-white uppercase tracking-tight leading-tight" data-id="element-752">
              {communicationData.currentPersona}
            </h2>

            {/* Metadata - Tertiary (text-sm text-gray-500) */}
            <div className="flex items-center gap-2 text-sm font-mono text-gray-400 leading-snug" data-id="element-753">
              <span className="font-medium text-[#FF0000]" data-id="element-754">
                {communicationData.confidence}% confidence
              </span>
              <span className="w-1 h-1 bg-gray-600" data-id="element-755" />
              <span data-id="element-756">Based on {communicationData.scanCount} scans</span>
            </div>
          </div>

          {/* Evolution Badge - Visual Cue */}
          {communicationData.recentlyEvolved && <motion.div initial={{
          scale: 0.9,
          opacity: 0
        }} animate={{
          scale: 1,
          opacity: 1
        }} className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-[#FF0000]/10 border border-[#FF0000] text-[#FF0000] text-xs font-mono uppercase tracking-widest" data-id="element-757">
              <Sparkles className="w-3.5 h-3.5" data-id="element-758" />
              <span data-id="element-759">Recently Evolved</span>
            </motion.div>}
        </div>

        {/* Latest Change Preview - Above Fold */}
        {topChange && <div className={`p-4 border space-y-2 ${topChange.direction === 'up' ? 'bg-green-500/5 border-green-500/20' : 'bg-orange-500/5 border-orange-500/20'}`} data-id="element-760">
            <div className="flex items-center gap-2" data-id="element-761">
              <span className="text-sm font-bold text-white uppercase tracking-wider leading-tight" data-id="element-762">
                Latest Detection:
              </span>
              <span className={`inline-flex items-center gap-1 px-2 py-0.5 border text-[10px] font-mono uppercase tracking-widest ${topChange.direction === 'up' ? 'bg-green-500/10 text-green-500 border-green-500/30' : 'bg-orange-500/10 text-orange-500 border-orange-500/30'}`} data-id="element-763">
                {topChange.direction === 'up' ? <TrendingUp className="w-3 h-3" data-id="element-764" /> : <TrendingDown className="w-3 h-3" data-id="element-765" />}
                {topChange.trait} {topChange.direction === 'up' ? '+' : '-'}
                {topChange.change}%
              </span>
            </div>
            {/* Description - Secondary (text-base text-gray-600) */}
            <p className="text-sm font-mono text-gray-400 leading-relaxed" data-id="element-766">
              {topChange.example}
            </p>
          </div>}
      </div>

      {/* SECONDARY: Trait Scores Section */}
      <div className="p-6 space-y-4 border-b border-gray-800" data-id="element-767">
        {/* Section Label - Tertiary */}
        <h3 className="text-[10px] font-mono text-gray-500 uppercase tracking-widest leading-tight" data-id="element-768">
          Behavioral Markers
        </h3>

        {/* Trait Cards - 8px Grid Aligned */}
        <div className="grid grid-cols-3 gap-4" data-id="element-769">
          {communicationData.traits.map(trait => {
          const Icon = trait.icon;
          return <div key={trait.name} className="text-center space-y-2" data-id="element-770">
                <div className="w-12 h-12 mx-auto flex items-center justify-center border" style={{
              '--trait-color': trait.color,
              '--trait-color-15': `${trait.color}15`,
              '--trait-color-50': `${trait.color}50`,
              backgroundColor: 'var(--trait-color-15)',
              borderColor: 'var(--trait-color-50)'
            } as React.CSSProperties} data-id="element-771">
                  <Icon className="w-5 h-5" style={{
                color: trait.color
              }} data-id="element-772" />
                </div>
                {/* Score - Primary for this context */}
                <div className="text-2xl font-black text-white font-mono leading-none" data-id="element-773">
                  {trait.currentValue}
                </div>
                {/* Label - Tertiary */}
                <div className="text-xs font-mono text-gray-500 uppercase tracking-widest leading-tight" data-id="element-774">
                  {trait.name}
                </div>
              </div>;
        })}
        </div>

        {/* Chart Section */}
        <div className="space-y-4 pt-2" data-id="element-775">
          <div className="flex items-center justify-between" data-id="element-776">
            <span className="text-[10px] font-mono text-gray-500 uppercase tracking-widest" data-id="element-777">
              Trend Analysis
            </span>

            {/* Legend */}
            <div className="flex gap-3" data-id="element-778">
              {communicationData.traits.map(trait => <div key={trait.name} className="flex items-center gap-1.5 text-[10px] font-mono text-gray-400 uppercase tracking-widest" data-id="element-779">
                  <span className="w-2 h-2" style={{
                backgroundColor: trait.color
              }} data-id="element-780" />
                  <span className="hidden sm:inline" data-id="element-781">{trait.name}</span>
                </div>)}
            </div>
          </div>

          {/* SVG Chart */}
          <div className="relative h-32 w-full" data-id="element-782">
            <svg viewBox={`0 0 ${width} ${height}`} className="w-full h-full overflow-visible" preserveAspectRatio="none" data-id="element-783">
              {/* Grid Lines - More Subtle */}
              {[0, 25, 50, 75, 100].map(tick => <line key={tick} x1="0" y1={getY(tick)} x2="100" y2={getY(tick)} stroke="currentColor" strokeOpacity="0.2" strokeWidth="0.2" className="text-gray-800" data-id="element-784" />)}

              {/* Data Lines */}
              {communicationData.traits.map((trait, traitIndex) => <g key={trait.name} data-id="element-785">
                  <path d={getPath(trait.values)} fill="none" stroke={trait.color} strokeWidth="1.5" strokeLinecap="square" strokeLinejoin="miter" data-id="element-786" />

                  {/* Interaction Points */}
                  {trait.values.map((val, pointIndex) => <circle key={pointIndex} cx={getX(pointIndex, trait.values.length)} cy={getY(val)} r="2" fill={trait.color} stroke="#0a0a0a" strokeWidth="0.5" className="cursor-pointer hover:r-3 transition-all duration-200" onMouseEnter={e => {
                const rect = e.currentTarget.getBoundingClientRect();
                setHoveredPoint({
                  traitIndex,
                  pointIndex,
                  value: val,
                  x: rect.left + rect.width / 2,
                  y: rect.top
                });
              }} onMouseLeave={() => setHoveredPoint(null)} data-id="element-787" />)}
                </g>)}
            </svg>

            {/* Tooltip */}
            <AnimatePresence data-id="element-788">
              {hoveredPoint && <motion.div initial={{
              opacity: 0,
              y: 5
            }} animate={{
              opacity: 1,
              y: 0
            }} exit={{
              opacity: 0,
              y: 5
            }} className="fixed z-50 px-2 py-1 bg-gray-900 border border-gray-700 text-white text-xs font-mono rounded-none shadow-lg pointer-events-none transform -translate-x-1/2 -translate-y-full mt-[-8px]" style={{
              left: hoveredPoint.x,
              top: hoveredPoint.y
            }} data-id="element-789">
                  <div className="font-bold uppercase tracking-wider leading-tight mb-1" data-id="element-790">
                    {communicationData.traits[hoveredPoint.traitIndex].name}
                  </div>
                  <div className="leading-tight text-gray-400" data-id="element-791">
                    Score:{' '}
                    <span className="text-white" data-id="element-792">{hoveredPoint.value}/100</span>
                  </div>
                </motion.div>}
            </AnimatePresence>
          </div>

          {/* X-Axis Labels - Tertiary */}
          <div className="flex justify-between text-[10px] font-mono text-gray-500 uppercase tracking-widest px-1" data-id="element-793">
            <span data-id="element-794">
              {communicationData.traits[0].values.length === 1 ? 'First scan' : `Last ${communicationData.traits[0].values.length} scans`}
            </span>
            <span data-id="element-795">Latest</span>
          </div>
        </div>
      </div>

      {/* PROGRESSIVE DISCLOSURE: Full Change History */}
      {communicationData.recentChanges.length > 0 && <div data-id="element-796">
          <button onClick={() => setIsExpanded(!isExpanded)} className="w-full flex items-center justify-between px-6 py-4 text-sm font-bold text-gray-400 hover:text-white hover:bg-gray-900 transition-colors group uppercase tracking-wider" data-id="element-797">
            <span className="flex items-center gap-2" data-id="element-798">
              <Sparkles className="w-4 h-4 text-[#FF0000] group-hover:text-[#FF0000] transition-colors" data-id="element-799" />
              View Full Behavioral Log
              <span className="text-xs font-mono text-gray-500" data-id="element-800">
                ({communicationData.recentChanges.length} detections)
              </span>
            </span>
            <motion.div animate={{
          rotate: isExpanded ? 180 : 0
        }} transition={{
          duration: 0.3
        }} data-id="element-801">
              <ChevronDown className="w-4 h-4 text-gray-500 group-hover:text-white transition-colors" data-id="element-802" />
            </motion.div>
          </button>

          <AnimatePresence data-id="element-803">
            {isExpanded && <motion.div initial={{
          height: 0,
          opacity: 0
        }} animate={{
          height: 'auto',
          opacity: 1
        }} exit={{
          height: 0,
          opacity: 0
        }} transition={{
          duration: 0.3,
          ease: 'easeInOut'
        }} className="overflow-hidden" data-id="element-804">
                <div className="px-6 pb-6 pt-2 space-y-3 bg-[#050505]" data-id="element-805">
                  {communicationData.recentChanges.map((change, i) => <div key={i} className="bg-[#0a0a0a] border border-gray-800 p-4 space-y-2" data-id="element-806">
                      <div className="flex items-center justify-between" data-id="element-807">
                        <div className="flex items-center gap-2" data-id="element-808">
                          {/* Change Title - Secondary */}
                          <span className="text-sm font-bold text-white uppercase tracking-wider leading-tight" data-id="element-809">
                            {change.trait}
                          </span>
                          {/* Badge - Semantic Color */}
                          <span className={`
                            inline-flex items-center gap-1 px-2 py-0.5 border text-[10px] font-mono uppercase tracking-widest
                            ${change.direction === 'up' ? 'bg-green-500/10 text-green-500 border-green-500/30' : 'bg-orange-500/10 text-orange-500 border-orange-500/30'}
                          `} data-id="element-810">
                            {change.direction === 'up' ? <TrendingUp className="w-3 h-3" data-id="element-811" /> : <TrendingDown className="w-3 h-3" data-id="element-812" />}
                            {change.direction === 'up' ? '+' : '-'}
                            {change.change}%
                          </span>
                        </div>
                        {/* Metadata - Tertiary */}
                        <span className="text-[10px] font-mono text-gray-500 uppercase tracking-widest" data-id="element-813">
                          Scan {change.scanRef}
                        </span>
                      </div>
                      {/* Description - Secondary */}
                      <p className="text-sm font-mono text-gray-400 leading-relaxed" data-id="element-814">
                        {change.example}
                      </p>
                    </div>)}
                </div>
              </motion.div>}
          </AnimatePresence>
        </div>}
    </div>;
}